import get from 'lodash.get';
import {
    Fragment,
    FunctionComponent,
    MouseEventHandler,
    ChangeEventHandler,
    FocusEvent,
    MouseEvent,
    ChangeEvent,
} from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { BoxSelectOption } from 'dibs-elements/exports/BoxSelect';
import { ACHFormType, FormKeys } from './utils/validations';

// components
import AccountType from './components/fields/AccountType';
import OwnershipType from './components/fields/OwnershipType';
import RoutingNumber from './components/fields/RoutingNumber';
import AccountNumber from './components/fields/AccountNumber';
import FirstName from './components/fields/FirstName';
import LastName from './components/fields/LastName';
import BusinessName from './components/fields/BusinessName';

import styles from './styles/ACHForm.scss';

type updateType = {
    field: string;
    value: string;
};

type AccountType = 'checking' | 'savings';
type OwnershipType = 'personal' | 'business';

export type ACHFormValue = {
    accountType: AccountType;
    ownershipType: OwnershipType;
    routingNumber?: string;
    accountNumber?: string;
    firstNameOnAccount?: string;
    lastNameOnAccount?: string;
    businessNameOnAccount?: string;
};

export type ACHBlurred = {
    [key in FormKeys]: boolean;
};

type ACHFormProps = {
    blurred: ACHBlurred;
    onBlurred?: (arg1: ACHBlurred) => void;
    onChange?: (arg1: updateType) => void;
    renderChildren?: () => void;
    formValue?: ACHFormValue;
    errors?: ACHFormType;
    disableForm?: boolean;
    inputsWrapperClass?: string;
    disabledFields?: string[];
};

export const defaultValue = {
    accountType: 'checking',
    ownershipType: 'personal',
    routingNumber: '',
    accountNumber: '',
    firstNameOnAccount: '',
    lastNameOnAccount: '',
    businessNameOnAccount: '',
};

export const defaultBlurred: ACHBlurred = {
    accountType: true,
    ownershipType: false,
    routingNumber: false,
    accountNumber: false,
    firstNameOnAccount: false,
    lastNameOnAccount: false,
    businessNameOnAccount: false,
};

const messages = defineMessages({
    checking: {
        id: 'achForm.CheckoutACHForm.checking',
        defaultMessage: 'Checking',
    },
    savings: {
        id: 'achForm.CheckoutACHForm.savings',
        defaultMessage: 'Savings',
    },
    personal: {
        id: 'achForm.CheckoutACHForm.personal',
        defaultMessage: 'Personal',
    },
    business: {
        id: 'achForm.CheckoutACHForm.business',
        defaultMessage: 'Business',
    },
});

const ACHForm: FunctionComponent<ACHFormProps> = ({
    blurred = defaultBlurred,
    onBlurred = () => null,
    onChange = () => null,
    renderChildren = () => null,
    formValue = defaultValue,
    errors,
    disableForm,
    disabledFields = [],
    inputsWrapperClass,
}) => {
    const intl = useIntl();
    const accountTypes: AccountType[] = ['checking', 'savings'];
    const ownershipTypes: OwnershipType[] = ['personal', 'business'];

    const createSelectOptions = (options: AccountType[] | OwnershipType[]): BoxSelectOption[] => {
        return options.map(opt => {
            const optionsObject = {
                label: intl.formatMessage(messages[opt]),
                value: opt,
            };
            return optionsObject as BoxSelectOption;
        });
    };

    const updateFormState = ({ field, value }: updateType): void => {
        const valueToUpdate = get(formValue, field);
        if (valueToUpdate !== value) {
            onChange({ field, value });
        }
    };

    const onUpdate: MouseEventHandler = (event: MouseEvent): void => {
        let field = get(event, 'currentTarget.name');
        const value = get(event, 'currentTarget.value');
        // check account type
        if (!field && accountTypes.find(type => type === value)) {
            field = 'accountType';
        }
        // check ownership type
        if (!field && ownershipTypes.find(type => type === value)) {
            field = 'ownershipType';
        }
        updateFormState({ field, value });
    };

    const onInputUpdate: ChangeEventHandler = (event: ChangeEvent): void => {
        const field = get(event, 'currentTarget.name');
        let value = get(event, 'currentTarget.value').trim();
        // numbers only
        if (field === 'accountNumber' || field === 'routingNumber') {
            value = value.replace(/\D/g, '');
        }
        // only 9 numbers
        if (field === 'routingNumber' && value.length > 9) {
            value = value.substring(0, 9);
        }
        // update state
        updateFormState({
            field,
            value,
        });
    };

    const onBlur = (event: FocusEvent<HTMLInputElement>): void => {
        const name = event.target.name as FormKeys;
        onBlurred({
            ...blurred,
            [name]: true,
        });
        onInputUpdate(event);
    };

    const sharedProps = {
        onChange: onInputUpdate,
        onBlur,
    };

    return (
        <div className={styles.achForm}>
            <>
                <div className={inputsWrapperClass}>
                    <div className={`${styles.group} ${styles.full}`}>
                        <AccountType
                            value={formValue.accountType}
                            onChange={onUpdate}
                            options={createSelectOptions(accountTypes)}
                            disabled={disableForm || disabledFields.includes('accountType')}
                        />
                    </div>
                    <div className={`${styles.group} ${styles.full}`}>
                        <RoutingNumber
                            {...sharedProps}
                            value={formValue.routingNumber}
                            isBlurred={get(blurred, 'routingNumber')}
                            error={get(errors, 'routingNumber')}
                            disabled={disableForm || disabledFields.includes('routingNumber')}
                        />
                    </div>
                    <div className={`${styles.group} ${styles.full}`}>
                        <AccountNumber
                            {...sharedProps}
                            value={formValue.accountNumber}
                            isBlurred={get(blurred, 'accountNumber')}
                            error={get(errors, 'accountNumber')}
                            disabled={disableForm || disabledFields.includes('accountNumber')}
                        />
                    </div>
                    <div className={`${styles.group} ${styles.full}`}>
                        <OwnershipType
                            value={formValue.ownershipType}
                            onChange={onUpdate}
                            options={createSelectOptions(ownershipTypes)}
                            disabled={disableForm || disabledFields.includes('ownershipType')}
                        />
                    </div>
                    {formValue.ownershipType !== 'business' && (
                        <Fragment>
                            <div className={styles.group}>
                                <FirstName
                                    {...sharedProps}
                                    value={formValue.firstNameOnAccount}
                                    isBlurred={get(blurred, 'firstNameOnAccount')}
                                    error={get(errors, 'firstNameOnAccount')}
                                    disabled={
                                        disableForm || disabledFields.includes('firstNameOnAccount')
                                    }
                                />
                                <LastName
                                    {...sharedProps}
                                    value={formValue.lastNameOnAccount}
                                    isBlurred={get(blurred, 'lastNameOnAccount')}
                                    error={get(errors, 'lastNameOnAccount')}
                                    disabled={
                                        disableForm || disabledFields.includes('lastNameOnAccount')
                                    }
                                />
                            </div>
                        </Fragment>
                    )}
                    {formValue.ownershipType === 'business' && (
                        <div className={`${styles.group} ${styles.full}`}>
                            <BusinessName
                                {...sharedProps}
                                value={formValue.businessNameOnAccount}
                                isBlurred={get(blurred, 'businessNameOnAccount')}
                                error={get(errors, 'businessNameOnAccount')}
                                disabled={
                                    disableForm || disabledFields.includes('businessNameOnAccount')
                                }
                            />
                        </div>
                    )}
                </div>
                {renderChildren()}
            </>
        </div>
    );
};

export default ACHForm;
